import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import classNames from 'classnames';
import groupBy from 'lodash.groupby';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { mapStyles } from 'src/constants/mapConstants';
import { ListTicket, MapFilter } from 'src/store/models/Ticket';
import { useTheme } from 'src/utils/useTheme';

import classes from './Map.module.scss';
import MapMarker from './MapMarker/MapMarker';

export interface MapsLocation {
  lat: number;
  lng: number;
  zoom?: number;
}

export interface MapProps {
  filter?: MapFilter;
  center: MapsLocation;
  markers?: ListTicket[];
  onBoundsChanged?: (map: google.maps.Map) => void;
  onZoomChanged?: (map: google.maps.Map) => void;
  onMarkerClick?: (ticket: ListTicket) => void;
  className?: string;
  zoom?: number;
}

const defaultCenter: MapsLocation = {
  lat: 46.119944,
  lng: 14.815333,
};

const Map: FC<MapProps> = ({
  filter,
  center,
  onBoundsChanged,
  onZoomChanged,
  markers,
  onMarkerClick,
  className,
  zoom = 10,
}) => {
  const firstTime = useRef(false);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAPS_API,
  });
  const theme = useTheme();

  const [map, setMap] = useState<google.maps.Map>(null);
  const [mapMarkers, setMapMarkers] = useState<ListTicket[]>([]);

  useEffect(() => {
    if (center.lat && center.lng && !firstTime.current) {
      map?.setCenter(center);
      firstTime.current = true;
    }
    // eslint-disable-next-line
  }, [center]);

  useEffect(() => {
    setMapMarkers([]);
    const groupedMarkers = groupBy(markers, (marker) => [marker?.lat, marker?.lon]);
    Object.entries(groupedMarkers)?.forEach(([key, value]) =>
      setMapMarkers((prev) => [...prev, value[value.length - 1]])
    );
  }, [markers]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map);
      if (center.lat && center.lng) {
        map.setCenter(center);
      }
    },
    [center]
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  return isLoaded ? (
    <GoogleMap
      mapContainerClassName={classNames(classes.Container, className)}
      center={defaultCenter}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onZoomChanged={() => onZoomChanged?.(map)}
      onBoundsChanged={() => onBoundsChanged?.(map)}
      options={{
        styles: mapStyles?.[theme],
        zoomControl: true,
        fullscreenControl: true,
        streetViewControl: false,
        mapTypeControl: false,
      }}
    >
      {mapMarkers?.map((ticket, i) => {
        const position = new google.maps.LatLng({
          lat: parseFloat(ticket?.lat),
          lng: parseFloat(ticket?.lon),
        });
        return <MapMarker key={i} position={position} ticket={ticket} onClick={onMarkerClick} />;
      })}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default memo(Map);
